import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import { PageShell } from "@/components/layout/PageShell";
import { GaplCountdown } from "@/components/common/GaplCountdown";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy, Info, MapPin, Shield, Users } from "lucide-react";

const GAPL_IMG = "https://images.unsplash.com/photo-1512719994953-eabf50895df7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwbWF0Y2glMjBwbGF5ZXIlMjBzdGFkaXVtJTIwdG91cm5hbWVudHxlbnwwfHx8fDE3OTEyNzM1Njd8MA&ixlib=rb-4.1.0&q=85";

const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");

const Gapl = () => {
  const { t, tf, lang, site } = useLang();
  const [gapl, setGapl] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => { api.gapl().then(setGapl); api.gaplSponsors().then(setSponsors).catch(() => setSponsors([])); }, []);
  if (!gapl) return <PageShell><div className="gmoaa-container py-20 text-center text-gmoaa-text-muted">Loading…</div></PageShell>;

  const heroImg = mediaUrl(site?.home?.gapl?.hero_image_url) || GAPL_IMG;

  // group fixtures by date
  const byDate = {};
  gapl.fixtures.forEach((f) => { (byDate[f.date] = byDate[f.date] || []).push(f); });
  const dates = Object.keys(byDate).sort();

  return (
    <PageShell>
      <div className="relative overflow-hidden bg-gmoaa-green">
        <img src={heroImg} alt="GAPL cricket" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative gmoaa-container py-14 text-white">
          <span className="inline-block rounded-full bg-gmoaa-gold px-3 py-1 text-xs font-bold text-gmoaa-green">SEASON {gapl.season}</span>
          <h1 className="mt-4 font-serif text-3xl font-bold sm:text-4xl lg:text-5xl">{tf(gapl, "season_name")}</h1>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-gmoaa-gold-light"><MapPin size={15} /> {tf(gapl, "venue")}</p>
          <div className="mt-6 max-w-md">
            <GaplCountdown startDate={gapl.start_date} />
          </div>
        </div>
      </div>

      <div className="gmoaa-container py-12">
        <div className="mb-6 flex items-center gap-2">
          <Shield size={18} className="text-gmoaa-saffron" />
          <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">{t("lastSeasonSquads")}</h2>
        </div>

        <Tabs defaultValue="squads">
          <TabsList className="bg-gmoaa-green-light">
            <TabsTrigger value="squads" className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white">{t("squads")}</TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white">{t("schedule")}</TabsTrigger>
            <TabsTrigger value="teams" className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white">{t("teams")}</TabsTrigger>
          </TabsList>

          <TabsContent value="squads" className="mt-5">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {gapl.teams.map((tm) => (
                <div key={tm.id} className="overflow-hidden rounded-xl border border-gmoaa-border-subtle bg-white">
                  <div className="flex items-center gap-3 border-b border-gmoaa-border-subtle bg-gmoaa-green-light px-4 py-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gmoaa-green text-white"><Trophy size={18} /></span>
                    <div>
                      <p className="font-serif font-semibold text-gmoaa-text-dark">{tf(tm, "name")}</p>
                      <p className="text-xs text-gmoaa-text-muted">{t("captain")}: <span className="font-medium text-gmoaa-saffron-hover">{tm.captain}</span></p>
                    </div>
                  </div>
                  <div className="p-4">
                    {tm.players && tm.players.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {tm.players.map((p, i) => (
                          <span key={`${p}-${i}`} className="rounded-full border border-gmoaa-border-subtle bg-gmoaa-surface px-2.5 py-1 text-xs text-gmoaa-text-dark">{p}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="flex items-center gap-1.5 text-xs text-gmoaa-text-muted"><Users size={13} /> Full squad to be published</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="schedule" className="mt-5 space-y-6">
            {dates.map((d, di) => (
              <div key={d}>
                <p className="mb-2 text-sm font-semibold text-gmoaa-green">
                  Day {di + 1} · {formatDate(d)}
                </p>
                <div className="space-y-2">
                  {byDate[d].map((f) => (
                    <div key={f.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3">
                      <div className="flex-1 text-right text-sm font-medium text-gmoaa-text-dark">{tf(f, "team_a")}</div>
                      <span className="mx-3 rounded-full bg-gmoaa-green-light px-2.5 py-0.5 text-xs font-bold text-gmoaa-green">VS</span>
                      <div className="flex-1 text-sm font-medium text-gmoaa-text-dark">{tf(f, "team_b")}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="teams" className="mt-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {gapl.teams.map((tm) => (
                <div key={tm.id} className="flex items-center gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-gmoaa-saffron-light text-gmoaa-saffron-hover"><Trophy size={20} /></span>
                  <div>
                    <p className="font-serif font-semibold text-gmoaa-text-dark">{tf(tm, "name")}</p>
                    <p className="text-xs text-gmoaa-text-muted">{t("captain")}: {tm.captain}</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-8 flex items-start gap-3 rounded-xl border border-dashed border-gmoaa-border-strong bg-gmoaa-green-light/40 p-5">
          <Info size={20} className="mt-0.5 shrink-0 text-gmoaa-green" />
          <p className="text-sm text-gmoaa-text-muted">{t("liveScoringNote")}</p>
        </div>

        {sponsors.length > 0 && (
          <div className="mt-10" data-testid="gapl-sponsors">
            <h2 className="mb-5 text-center font-serif text-2xl font-semibold text-gmoaa-green">{t("matchSponsors")}</h2>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {sponsors.map((s) => {
                const inner = (
                  <>
                    {s.logo_url ? <img src={mediaUrl(s.logo_url)} alt={s.name_en} className="max-h-10 max-w-full object-contain" /> : null}
                    <span className="text-xs font-medium text-gmoaa-text-muted">{lang === "gu" ? (s.name_gu || s.name_en) : s.name_en}</span>
                  </>
                );
                const cls = "flex h-20 w-44 flex-col items-center justify-center gap-1.5 rounded-lg border border-gmoaa-border-subtle bg-white px-3 py-2 text-center";
                return s.link ? (
                  <a key={s.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/gapl-sponsors/${s.id}/click`} target="_blank" rel="noopener noreferrer"
                    className={`${cls} transition-shadow hover:shadow-md`} data-testid={`gapl-sponsor-link-${s.id}`}>{inner}</a>
                ) : (
                  <div key={s.id} className={cls}>{inner}</div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Gapl;
